import React, { useState, useEffect } from "react"
import {
  Form,
  Input,
  Button,
  Checkbox,
  message as antdMessage,
  Spin,
} from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"
import Cookies from "js-cookie"
import { setUserCookies } from "../../utils/Cookies"
import { authApi } from "../../Apis/authApis"
import { fetchUserDetails } from "../../ReduxToolKit/Slice/UserSlice"
import { useDispatch } from "react-redux"
import useLanguage from "../../services/useLanguage"
import ToggleLogin from "../../components/Common/ToggleLogin"
import PrivacyPolicyModal from "../../components/Common/Modals/PrivacyPolicyModal"
import TermsOfServiceModal from "../../components/Common/Modals/TermsOfServiceModal"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"
import { getMarketingWebsiteUrl } from "../../utils/getMarketingWebsiteUrl"
import { jwtDecode } from "jwt-decode"
import { getPostLoginPathFromProfile } from "../../utils/modulePermissionSidebar"
import { ORGANIZATION_HOME_PATH, canAccessOrganizationRoutes, hasMerchantPortalAccess, isNoActiveMerchantPortalAccount } from "../../utils/organizationAccess"
import { probeOrganizationOwner } from "../../ReduxToolKit/Slice/OrganizationSlice"
import { getLocalizedPath } from "../../utils/authLanguageRoutes"

const Login = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const dispatch = useDispatch()
  const { language, handleLanguageChange } = useLanguage()
  const lang = getAppLanguage()
  const loginPage = translations[lang]?.loginPage
  const { AnalyticsPages } = translations[lang]
  const password = translations[lang]?.reset_password
  const commonErrorMsg = translations[lang]?.common_error_msg
  const websiteUrl = getMarketingWebsiteUrl(lang)

  useEffect(() => {
    if (searchParams.get("reason") === "no_merchant_access") {
      const message = loginPage?.no_merchant_access
      if (message) antdMessage.warning(message)
    }
  }, [searchParams, language, loginPage?.no_merchant_access])

  const onFinish = async (values) => {
    const payload = {
      email: values.email,
      password: values.password,
    }

    try {
      setLoading(true)
      const response = await authApi.login(payload)

      if (response.status === 200) {
        const { access } = response.data
        const loginUser = { token: access }

        setUserCookies(loginUser)
        const profile = await dispatch(fetchUserDetails()).unwrap()
        const orgProbe = await dispatch(probeOrganizationOwner(profile)).unwrap()

        if (!hasMerchantPortalAccess(profile, access, orgProbe?.isOwner)) {
          Cookies.remove("token")
          if (isNoActiveMerchantPortalAccount(profile, access, orgProbe?.isOwner)) {
            antdMessage.warning(loginPage?.no_merchant_access)
          }
          return
        }

        antdMessage.success(AnalyticsPages?.welcome)
        const redirectParam = searchParams.get("redirect")
        const safeRedirect =
          redirectParam &&
          redirectParam.startsWith("/") &&
          !redirectParam.startsWith("//")
            ? redirectParam
            : null

        const goAfterLogin = (path) => {
          navigate(getLocalizedPath(path, getAppLanguage()), { replace: true })
        }

        if (canAccessOrganizationRoutes(profile, orgProbe?.isOwner)) {
          goAfterLogin(safeRedirect || ORGANIZATION_HOME_PATH)
        } else if (safeRedirect) {
          goAfterLogin(safeRedirect)
        } else {
          let target = getPostLoginPathFromProfile(profile)
          try {
            const decoded = jwtDecode(access)
            const mr = String(decoded?.market_role ?? "")
            if (mr === "2") target = "/merchants"
          } catch {
            /* use profile path */
          }
          goAfterLogin(target)
        }
        form.resetFields()
      }
    } catch (error) {
      // Never keep a partial session on auth failure
      Cookies.remove("token")

      const rawDetail = error.response?.data?.detail
      const detail = Array.isArray(rawDetail) ? rawDetail[0] : rawDetail
      const detailText =
        typeof detail === "string"
          ? detail
          : typeof detail?.message === "string"
            ? detail.message
            : ""
      const isInvalidCredentials =
        error.response?.status === 401 ||
        /no active account found with the given credentials/i.test(
          detailText
        )

      let message
      if (isInvalidCredentials) {
        message = loginPage?.invalid_credentials || detailText
      } else if (lang === "en") {
        message = detailText
      } else {
        message =
          error.response?.data?.detailArabic || detailText
      }

      antdMessage.error(message || loginPage?.invalid_credentials)
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase()

    if (value.includes("+")) {
      antdMessage.error(commonErrorMsg?.email_cannot_symbol)
      return
    }

    form.setFieldsValue({ email: value })
  }

  return (
    <>
      <div className="signup-inner">
        <div className="signup-left">
          <div className="signup-left-logo">
            <a href={websiteUrl}>
              <img src="/signup/Zory brown.svg" alt="Zory Logo" />
            </a>
          </div>
        </div>

        <div className="signup-right">
          <div className="sign-l-header" dir={lang === "ar" ? "rtl" : "ltr"}>
            <button
              className="back-arrow-login"
              onClick={() => (window.location.href = websiteUrl)}
            >
              {language === "ar" ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="back-arrow-login-label">رجوع</span>
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="back-arrow-login-label">Back</span>
                </>
              )}
            </button>
            <div className="logo-sign">
              <a href={websiteUrl}>
                <img src="/signup/Zorysmall.svg" alt="Zory Logo" />
              </a>
            </div>
            <div className="lang-toggle">
              <ToggleLogin
                toggleLanguage={handleLanguageChange}
                currentLanguage={language}
              />
            </div>
          </div>

          <div className="content">
            <h3 className="font-Montserrat00">
              {lang === "ar"
                ? "بوابة تسجيل دخول البائع"
                : "Login Vendor Portal"}
            </h3>
            <span className="font-Montserrat-300">
              {lang === "ar"
                ? "يرجى إدخال بياناتك لتسجيل الدخول"
                : "Please enter your details to log in"}
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ agreeToTerms: true }}
            style={{ marginTop: "24px" }}
          >
            <Form.Item
              label={
                <span style={lang === "ar" ? { fontSize: "13px" } : undefined}>
                  {lang === "ar" ? "بريد إلكتروني" : "Email"}
                </span>
              }
              name="email"
              rules={[
                {
                  required: true,
                  message: loginPage?.email_error_1,
                },
                {
                  type: "email",
                  message: loginPage?.email_error_2,
                },
                {
                  validator: (_, value) =>
                    value && value.includes("+")
                      ? Promise.reject(commonErrorMsg?.email_cannot_symbol)
                      : Promise.resolve(),
                },
              ]}
              labelCol={{ span: 24 }}
            >
              <Input
                className="custom-input"
                placeholder={loginPage?.email_placeholder}
                onChange={handleEmailChange}
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(132, 135, 136, 0.25)",
                  height: 50,
                  borderRadius: 12,
                }}
              />
            </Form.Item>

            <div className="mobile-center-password">
              <Form.Item
                label={
                  <span
                    style={lang === "ar" ? { fontSize: "13px" } : undefined}
                  >
                    {lang === "ar" ? "كلمة المرور" : "Password"}
                  </span>
                }
                name="password"
                rules={[
                  {
                    required: true,
                    message: password?.password_error,
                  },
                ]}
                labelCol={{ span: 24 }}
              >
                <Input.Password
                  className="custom-input"
                  placeholder={loginPage?.password_placeholder}
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(132, 135, 136, 0.25)",
                    height: 50,
                    borderRadius: 12,
                  }}
                />
              </Form.Item>
            </div>

            <div className="login-terms-row">
              <Form.Item
                name="agreeToTerms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(loginPage?.agree_error),
                  },
                ]}
                className="login-terms-form-item"
              >
                <Checkbox className="login-terms-checkbox">
                  <span className="login-terms-text">
                    {loginPage?.agree_prefix}{" "}
                    <button
                      type="button"
                      className="login-terms-link-btn"
                      onClick={(e) => {
                        e.preventDefault()
                        setIsTermsModalOpen(true)
                      }}
                    >
                      {loginPage?.terms_of_service}
                    </button>{" "}
                    {loginPage?.and}{" "}
                    <button
                      type="button"
                      className="login-terms-link-btn"
                      onClick={(e) => {
                        e.preventDefault()
                        setIsPrivacyModalOpen(true)
                      }}
                    >
                      {loginPage?.privacy_policy_link}
                    </button>
                  </span>
                </Checkbox>
              </Form.Item>

              <a href="/forget-password" className="login-forgot-password">
                {loginPage?.forget_password}
              </a>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  fontSize: "18px",
                  height: 50,
                  borderRadius: 12,
                  background: "#1d869a",
                  border: "none",
                }}
              >
                {loginPage?.login}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />

      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />
    </>
  )
}

export default Login
